import { useQuery } from '@tanstack/react-query';
import api from 'src/app/core/api/apiProvider';

export const contentSummaryQueryKey = (id: string | number) => [
  'contentSummary',
  id,
];

/**
 * `GET /contents/{id}/summary/` (trailing slash). `summary` is `null` when the
 * content has none. A 404 only means the content id does not exist.
 */
export const fetchContentSummary = async (
  id: string | number
): Promise<string | null> => {
  const { data } = await api.get(
    `${import.meta.env.VITE_API_URL}/contents/${id}/summary/`
  );

  return typeof data?.summary === 'string' ? data.summary : null;
};

/**
 * Summary of one content, fetched lazily. Pass `enabled: false` until the
 * summary is actually needed so unopened cards trigger no requests. Summaries
 * are stored by the backend and do not change, so a cached one is never stale.
 */
const useContentSummary = (id: string | number, enabled: boolean) =>
  useQuery({
    queryKey: contentSummaryQueryKey(id),
    queryFn: () => fetchContentSummary(id),
    enabled,
    staleTime: Infinity,
    // One retry for transient failures, none for a 4xx (unknown id, auth):
    // the modal should show its error copy quickly, not after the default 3.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) {
        return false;
      }
      return failureCount < 1;
    },
  });

export default useContentSummary;
